import { useState, useEffect } from 'react'

/**
 * Hook che monitora il completamento del Largest Contentful Paint (LCP)
 * utilizzando l'API PerformanceObserver.
 * @returns {boolean} - true quando LCP è stato completato, altrimenti false
 */
function useLCPComplete() {
  const [lcpComplete, setLcpComplete] = useState(false)

  useEffect(() => {
    // Se PerformanceObserver non è supportato, consideriamo il LCP completato
    if (!window.PerformanceObserver) {
      setLcpComplete(true)
      return () => {}
    }

    let lcpObserver = null

    // Funzione che gestisce l'evento LCP
    const handleLCP = (entryList) => {
      const entries = entryList.getEntries()

      if (entries.length > 0) {
        // LCP è stato rilevato
        setLcpComplete(true)

        // Disconnette l'observer una volta che abbiamo ottenuto ciò che ci serve
        if (lcpObserver) {
          lcpObserver.disconnect()
        }
      }
    }

    // Creiamo un observer per monitorare gli eventi LCP
    lcpObserver = new PerformanceObserver(handleLCP)

    try {
      // Osserva gli eventi 'largest-contentful-paint'
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })
    } catch (error) {
      // Se non riusciamo a osservare l'LCP, consideriamolo completato
      console.error('LCP observer error:', error)
      setLcpComplete(true)
    }

    // Fallback: se dopo 5 secondi l'LCP non è ancora stato rilevato
    // consideriamolo completato comunque
    const timeoutId = setTimeout(() => {
      setLcpComplete(true)
      if (lcpObserver) {
        lcpObserver.disconnect()
      }
    }, 5000)

    // Cleanup
    return () => {
      clearTimeout(timeoutId)
      if (lcpObserver) {
        lcpObserver.disconnect()
      }
    }
  }, [])

  return lcpComplete
}

export default useLCPComplete
